import React, { useRef } from 'react';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, PlusIcon, ArrowUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

/* The product surfaces, as a horizontal rail. A rail rather than a grid
 because these are peers with no ranking between them, and each one needs a
 tall inner visual that a three-across grid would squash. */

type Card = {
  title: string;
  blurb: string;
  to: string;
  bg: string;
  text: string;
  muted: string;
  chip: string;
  visual: React.ReactNode;
};
function ChatVisual() {
  return <div className="rounded-xl border border-black/5 bg-white p-3 shadow-lg" data-id="element-3116" data-mp="1gc9910b1p0xo">
      <div className="flex items-center gap-2" data-id="element-3117" data-mp="1v4b5099zv9sf">
        <span className="flex h-5 w-5 items-center justify-center rounded bg-[#2383E2] text-[10px] font-bold text-white" data-id="element-3118" data-mp="1qbqcdb2tr2qh">
          N
        </span>
        <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-3119" data-mp="12kuryz0p79t9">
          Q3 pipeline review
        </span>
      </div>
      <p className="mt-3 text-[13px] text-neutral-400" data-id="element-3120" data-mp="dgcs2aadp011">
        Ask Giga anything…
      </p>
      <div className="mt-4 flex items-center gap-2" data-id="element-3121" data-mp="dejs1du9te20">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100" data-id="element-3122" data-mp="hgn7edk292sk">
          <PlusIcon size={13} className="text-neutral-500" data-id="element-3123" data-mp="m9zjjo7jzh4f" />
        </span>
        <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11.5px] font-medium text-neutral-600" data-id="element-3124" data-mp="r8aslwq0xnll">
          Company brain
        </span>
        <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-3125" data-mp="3qopk7gju6wk9">
          <ArrowUpIcon size={13} strokeWidth={3} className="text-white" data-id="element-3126" data-mp="3uqkkqity15wz" />
        </span>
      </div>
    </div>;
}
function WorkflowVisual() {
  return <div className="space-y-3" data-id="element-3127" data-mp="8ht5q8rvhm85">
      {['Watch #support for escalations', 'Summarise the thread', 'File a Linear ticket'].map((s, i) => <div key={s} className="flex items-center gap-3 rounded-lg border border-black/5 bg-white px-3 py-2.5 shadow-sm" data-id="element-3128" data-mp="1ewkd8vbi45b5" data-mpi={i}>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[10px] font-bold text-white" data-id="element-3129" data-mp="1jyjfu2zf2y71">
            {i + 1}
          </span>
          <span className="text-[12.5px] font-medium text-[#2B211A]" data-id="element-3130" data-mp="p8expxtd4oxd">{s}</span>
        </div>)}
      <div className="rounded-lg border border-dashed border-black/15 px-3 py-2.5 text-[12.5px] font-medium text-black/40" data-id="element-3131" data-mp="1455x4731e0mz">
        + Add a step
      </div>
    </div>;
}
function AgentVisual() {
  return <ol className="space-y-4" data-id="element-3132" data-mp="bil5d5infmiw">
      {[{
      label: 'Searched Drive',
      detail: 'Q3 forecast.xlsx'
    }, {
      label: 'Read 2 documents',
      detail: 'Renewal terms.docx'
    }].map(s => <li key={s.label} className="flex gap-3" data-id="element-3133" data-mp="l7uwoj5o515y">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-3134" data-mp="l9pli8k29dfb">
            <CheckIcon size={11} strokeWidth={3.5} className="text-white" data-id="element-3135" data-mp="3owpwvyf0hejr" />
          </span>
          <span data-id="element-3136" data-mp="3oud4ssfjhnsz">
            <span className="block text-[13px] font-semibold text-[#2B211A]" data-id="element-3137" data-mp="3jxv361fia5kv">
              {s.label}
            </span>
            <span className="mt-1 inline-block rounded bg-black/5 px-1.5 py-0.5 text-[11.5px] text-[#2B211A]/70" data-id="element-3138" data-mp="1zfasggtbi0bd">
              {s.detail}
            </span>
          </span>
        </li>)}
      <li className="flex items-center gap-3 text-[13px] font-semibold text-[#2B211A]/45" data-id="element-3139" data-mp="1phwqfm855e2l">
        <span className="h-5 w-5 rounded-full border-2 border-dashed border-[#2B211A]/25" data-id="element-3140" data-mp="1ug7ybrevlei6" />
        Drafting the answer
      </li>
    </ol>;
}
const TOOL_ICONS = ['slack.com', 'notion.so', 'github.com', 'salesforce.com', 'hubspot.com', 'google.com', 'linear.app', 'figma.com', 'stripe.com', 'atlassian.com', 'dropbox.com', 'zoom.us'];
function IntegrationVisual() {
  return <div data-id="element-3141" data-mp="297gjnobqwdpi">
      <div className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-semibold text-white/80" data-id="element-3142" data-mp="297qf3rfk7d2l">
        200+ integrations
      </div>
      <div className="grid grid-cols-4 gap-2.5" data-id="element-3143" data-mp="xpfj0vzrnqhg">
        {TOOL_ICONS.map(domain => <span key={domain} className="flex h-11 items-center justify-center rounded-lg bg-white/10" data-id="element-3144" data-mp="11s2dl4ntnpic">
            <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt="" aria-hidden="true" className="h-5 w-5 rounded object-contain" data-id="element-3145" data-mp="1hz88i3bpq3nn" />
          </span>)}
      </div>
    </div>;
}
function ApiVisual() {
  return <pre className="overflow-hidden rounded-xl bg-black/40 p-4 text-[11.5px] leading-relaxed text-white/70" data-id="element-3146" data-mp="189oqt0zqkfud">
      <code data-id="element-3147" data-mp="13bvlsba65rvo">{`curl --request POST \\
  --url https://api.getgiga.com/v1/ask \\
  --header 'Authorization: Bearer …' \\
  --data '{
    "workspace": "acme",
    "question": "Which renewals
      slip this quarter?"
  }'`}</code>
    </pre>;
}
const CARDS: Card[] = [{
  title: 'Chat',
  blurb: 'Grounded in your systems, for everyone in the company.',
  to: '/company-brain',
  bg: 'bg-[#3D2F25]',
  text: 'text-white',
  muted: 'text-white/60',
  chip: 'bg-white/15 text-white',
  visual: <ChatVisual data-id="element-3148" data-mp="fl9ckgofi1k3" />
}, {
  title: 'Workflows',
  blurb: 'Recurring work, run end to end.',
  to: '/app-screen-mockups/workflow-show',
  bg: 'bg-[#C9D4D6]',
  text: 'text-[#2B211A]',
  muted: 'text-[#2B211A]/60',
  chip: 'bg-black/10 text-[#2B211A]',
  visual: <WorkflowVisual data-id="element-3149" data-mp="2r7jy7j7qhl6y" />
}, {
  title: 'Agents',
  blurb: 'Custom coworkers for a single job.',
  to: '/unlisted/agents',
  bg: 'bg-[#E3DCCE]',
  text: 'text-[#2B211A]',
  muted: 'text-[#2B211A]/60',
  chip: 'bg-black/10 text-[#2B211A]',
  visual: <AgentVisual data-id="element-3150" data-mp="30yem0s0mosui" />
}, {
  title: 'Integrations',
  blurb: 'Connects to the tools you already run on.',
  to: '/integrations',
  bg: 'bg-[#1F3A3D]',
  text: 'text-white',
  muted: 'text-white/60',
  chip: 'bg-white/15 text-white',
  visual: <IntegrationVisual data-id="element-3151" data-mp="3axwzqqpy3ie4" />
}, {
  title: 'API',
  blurb: 'Bring Giga into your own applications.',
  to: '/developers',
  bg: 'bg-[#1A1512]',
  text: 'text-white',
  muted: 'text-white/60',
  chip: 'bg-white/15 text-white',
  visual: <ApiVisual data-id="element-3152" data-mp="1kjmuehbb3pm9" />
}];
export function StackCarousel() {
  const rail = useRef<HTMLDivElement>(null);
  const scrollBy = (dir: 1 | -1) => {
    rail.current?.scrollBy({
      left: dir * 380,
      behavior: 'smooth'
    });
  };
  return <section className="py-20 md:py-28" data-id="element-3153" data-mp="2su6gkj9h6vnn">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10" data-id="element-3154" data-mp="2sv82xjvxz4ko">
        <h2 className="font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-3155" data-mp="2nvloj74dniaa">
          The essential AI stack for your company.
          <span className="block text-neutral-400" data-id="element-3156" data-mp="2wv8ij7us1ltb">
            Simple for business users. Ready for advanced use cases.
          </span>
        </h2>
      </div>

      <div ref={rail} className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-6 pb-4 [scrollbar-width:none] md:px-10 [&::-webkit-scrollbar]:hidden" data-id="element-3157" data-mp="3bqeqw1hchwpt">
        {/* Rail is intentionally wider than the text column so cards bleed
         off the right edge, signalling there is more to scroll. */}
        {CARDS.map(card => <article key={card.title} className={`flex w-[300px] shrink-0 snap-start flex-col rounded-2xl p-7 md:w-[360px] ${card.bg} ${card.text}`} data-id="element-3158" data-mp="3uih9hidjrn2p">
            <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-3159" data-mp="3pl7eln7e2j7h">
              {card.title}
            </h3>
            <p className={`mt-2 min-h-[48px] leading-relaxed ${card.muted}`} data-id="element-3160" data-mp="cy7e6un6e4ju">
              {card.blurb}
            </p>
            <Link to={card.to} aria-label={`More about ${card.title}`} className={`mt-5 flex h-9 w-9 items-center justify-center rounded-full transition-transform hover:scale-105 ${card.chip}`} data-id="element-3161" data-mp="32485dp7dlrm">
              <ArrowRightIcon size={16} strokeWidth={2.5} data-id="element-3162" data-mp="3qf80j303a54x" />
            </Link>
            <div className="mt-auto pt-10" data-id="element-3163" data-mp="3qd3lbowqpjpc">{card.visual}</div>
          </article>)}
      </div>

      <div className="mx-auto mt-6 flex max-w-[1100px] justify-end gap-2 px-6 md:px-10" data-id="element-3164" data-mp="3v7razr3hck5i">
        <button type="button" onClick={() => scrollBy(-1)} aria-label="Previous" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#2B211A]/12 text-[#2B211A] transition-colors hover:bg-[#2B211A]/5" data-id="element-3165" data-mp="3genuzo5jeegw">
          <ChevronLeftIcon size={18} data-id="element-3166" data-mp="3bhfoiktr3yvh" />
        </button>
        <button type="button" onClick={() => scrollBy(1)} aria-label="Next" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#2B211A]/12 text-[#2B211A] transition-colors hover:bg-[#2B211A]/5" data-id="element-3167" data-mp="3bh5safi44vhc">
          <ChevronRightIcon size={18} data-id="element-3168" data-mp="3mw97ypdmnxfu" />
        </button>
      </div>
    </section>;
}